import { SystemModule } from '@admin/manage-customer/models/user';
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  CaseReducer,
} from '@reduxjs/toolkit';
import {
  CurrentModule,
  DataModules,
  Module,
  ParamsGetModule,
} from '../models/module';
import { moduleServices } from '../services/module';
import { handleErrorResponse } from '@app/utils/helpers';
import { RequestLoading } from '@app/utils/constants';

interface ModalVisible {
  modal?: 'Create' | 'Edit';
  open: boolean;
}

const requests = [
  'createModule',
  'getModules',
  'getCurrentModules',
  'updateModuleStatus',
  'getModuleById',
  'updateModuleById',
] as const;

type ModuleRequest = RequestLoading<(typeof requests)[number]>;
interface State {
  loading: boolean;
  dataModules: DataModules;
  getModuleLoading: boolean;
  systemModuleList: Module[] | [];
  selectedModule: Module | undefined;
  moduleCurrent: CurrentModule[];
  moduleRequest: ModuleRequest;
  modalVisible: ModalVisible;
}

const initialState: State = {
  selectedModule: undefined,
  loading: false,
  dataModules: {
    data: [],
    paging: undefined,
  },
  systemModuleList: [],
  getModuleLoading: false,
  moduleCurrent: [],
  moduleRequest: {
    loading: false,
  },
  modalVisible: {
    open: false,
  },
};

type CR<T> = CaseReducer<State, PayloadAction<T>>;
const TYPE_PREFIX = 'admin/management-module/module/';

// AsyncThunk
const getModules = createAsyncThunk<DataModules, ParamsGetModule>(
  TYPE_PREFIX + 'getModules',
  async (params: ParamsGetModule, { rejectWithValue }) => {
    try {
      const result = await moduleServices.getModules(params);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const createNewModule = createAsyncThunk<Module, FormData>(
  TYPE_PREFIX + 'createNewModule',
  async (data, { rejectWithValue }) => {
    try {
      const result = await moduleServices.createModule(data);

      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const updateModuleById = createAsyncThunk<
  Module,
  { id: number; data: FormData }
>(
  TYPE_PREFIX + 'updateModuleById',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await moduleServices.updateModule(id, data);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const getSystemModuleList = createAsyncThunk<DataModules, ParamsGetModule>(
  TYPE_PREFIX + 'getSystemModuleList',
  async (params: ParamsGetModule, { rejectWithValue }) => {
    try {
      const result = await moduleServices.getModules(params);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const getModuleDetail = createAsyncThunk<Module, string>(
  TYPE_PREFIX + 'getModuleDetail',
  async (id, { rejectWithValue }) => {
    try {
      const result = await moduleServices.getModuleDetail(id);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const getModuleCurrent = createAsyncThunk<SystemModule[], void>(
  TYPE_PREFIX + 'getModuleCurrent',
  async (_, { rejectWithValue }) => {
    try {
      const result = await moduleServices.getModuleCurrent();
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const updateModuleStatus = createAsyncThunk<
  Module,
  { id: number; data: FormData }
>(
  TYPE_PREFIX + 'updateModuleStatus',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await moduleServices.updateModuleStatus(id, data);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const setModalVisibleCR: CR<ModalVisible> = (state, action) => ({
  ...state,
  modalVisible: action.payload,
});

const slice = createSlice({
  name: 'admin/module',
  initialState,
  reducers: {
    setSelectedModule: (state, action) => {
      // state.selectedModule = action.payload;
    },
    setModalVisible: setModalVisibleCR,
  },
  extraReducers: (builder) => {
    //getModule
    builder.addCase(getModules.pending, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: true,
        name: 'getModules',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getModules.fulfilled, (state, action) => ({
      ...state,
      dataModules: action.payload,
      moduleRequest: {
        loading: false,
        name: 'getModules',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getModules.rejected, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'getModules',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // create new module
    builder.addCase(createNewModule.pending, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: true,
        name: 'createModule',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(createNewModule.fulfilled, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'createModule',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(createNewModule.rejected, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'createModule',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    //getSystemModuleList
    builder.addCase(getSystemModuleList.pending, (state) => ({
      ...state,
      getModuleLoading: true,
    }));
    builder.addCase(getSystemModuleList.fulfilled, (state, { payload }) => ({
      ...state,
      systemModuleList: payload.data,
      getModuleLoading: false,
    }));
    builder.addCase(getSystemModuleList.rejected, (state) => ({
      ...state,
      getModuleLoading: true,
    }));

    //getModuleDetail
    builder.addCase(getModuleDetail.pending, (state, action) => ({
      ...state,
      // getModuleLoading: true,
      moduleRequest: {
        loading: true,
        name: 'getModuleById',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getModuleDetail.fulfilled, (state, action) => ({
      ...state,
      selectedModule: action.payload,
      moduleRequest: {
        loading: false,
        name: 'getModuleById',
        status: action.meta.requestStatus,
      },
      // getModuleLoading: false,
    }));
    builder.addCase(getModuleDetail.rejected, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'getModuleById',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
      // getModuleLoading: true,
    }));

    //getModuleCurrent
    builder.addCase(getModuleCurrent.pending, (state, action) => ({
      ...state,
      moduleRequest: {
        name: 'getCurrentModules',
        status: action.meta.requestStatus,
        loading: true,
      },
    }));
    builder.addCase(getModuleCurrent.fulfilled, (state, action) => ({
      ...state,
      moduleCurrent: action.payload,
      moduleRequest: {
        name: 'getCurrentModules',
        status: action.meta.requestStatus,
        loading: false,
      },
    }));
    builder.addCase(getModuleCurrent.rejected, (state, action) => ({
      ...state,
      moduleRequest: {
        name: 'getCurrentModules',
        status: action.meta.requestStatus,
        loading: false,
        error: action.payload ?? action.error,
      },
    }));
    // update module status by id
    builder.addCase(updateModuleStatus.pending, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: true,
        name: 'updateModuleStatus',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateModuleStatus.fulfilled, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'updateModuleStatus',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateModuleStatus.rejected, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'updateModuleStatus',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // update module by id
    builder.addCase(updateModuleById.pending, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: true,
        name: 'updateModuleById',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateModuleById.fulfilled, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'updateModuleById',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateModuleById.rejected, (state, action) => ({
      ...state,
      moduleRequest: {
        loading: false,
        name: 'updateModuleById',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));
  },
});

export const { setSelectedModule, setModalVisible } = slice.actions;

export {
  getModules,
  getSystemModuleList,
  getModuleDetail,
  getModuleCurrent,
  updateModuleStatus,
  createNewModule,
  updateModuleById,
};

export default slice.reducer;
